<template>
  <div class="wizard-stepper rounded-xl" :class="rootClass">
    <!-- 参考：侧栏垂直步骤条 -->
    <ol v-if="variant === 'sidebar'" class="sidebar-rail">
      <li v-for="(s, i) in steps" :key="s.key" class="sidebar-step">
        <div class="sidebar-step__track" aria-hidden="true">
          <div v-if="i > 0" class="sidebar-step__connector sidebar-step__connector--up"
            :class="{ 'is-done': i <= modelValue }" />
          <span class="sidebar-step__node" :class="{
            'is-done': i < modelValue,
            'is-active': i === modelValue,
            'is-pending': i > modelValue,
          }">
            <span class="sidebar-step__node-num">{{ i + 1 }}</span>
          </span>
          <div v-if="i < steps.length - 1" class="sidebar-step__connector sidebar-step__connector--down"
            :class="{ 'is-done': i < modelValue }" />
        </div>
        <button type="button" class="sidebar-step__body" :class="{
          'is-active': i === modelValue,
          'is-done': i < modelValue,
          'is-pending': i > modelValue,
        }" @click="$emit('update:modelValue', i)">
          <span class="sidebar-step__title">{{ s.title }}</span>
          <span class="sidebar-step__desc">{{ s.desc }}</span>
        </button>
      </li>
    </ol>

    <!-- 横向步骤（内联 / 卡片） -->
    <div v-else class="stepper-track d-flex">
      <button v-for="(s, i) in steps" :key="s.key" type="button" class="step-card text-left" :class="{
        'is-active': i === modelValue,
        'is-done': i < modelValue,
        'is-pending': i > modelValue,
      }" :title="`${s.title}：${statusText(i)} — ${s.desc}`" @click="$emit('update:modelValue', i)">
        <div class="step-headline d-flex align-center flex-wrap ga-2 min-width-0">
          <span class="step-num flex-shrink-0" aria-hidden="true">{{ i + 1 }}</span>
          <span class="step-headline-text text-truncate min-width-0">
            {{ s.title }}：{{ statusText(i) }}
          </span>
        </div>
        <p class="step-desc mb-0" :class="{ 'step-desc--sr': inline }">{{ s.desc }}</p>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  steps: { type: Array, required: true },
  modelValue: { type: Number, required: true },
  /** 与标题同排展示时收紧边距 */
  inline: { type: Boolean, default: false },
  /** track：横向；sidebar：侧栏垂直 */
  variant: { type: String, default: 'track' },
})

defineEmits(['update:modelValue'])

const rootClass = computed(() => {
  if (props.variant === 'sidebar') {
    return 'wizard-stepper--sidebar pa-1'
  }
  return props.inline ? 'wizard-stepper--inline pa-2' : 'gm-card mb-4 pa-4'
})

function statusText(i) {
  if (props.variant === 'sidebar') {
    if (i < props.modelValue) return '已完成'
    if (i === props.modelValue) return props.steps[i]?.activeStatus ?? '进行中'
    return '未开始'
  }
  if (i < props.modelValue) return '已配置'
  if (i === props.modelValue) return props.steps[i]?.activeStatus ?? '正在配置'
  return '未配置'
}

</script>

<style scoped lang="scss">
/* 与 variables.scss --color-primary / --color-text 及运维模块 gm-card 体系统一 */

/* 标题 + 说明 */
.wizard-stepper {
  width: 100%;
}

.wizard-stepper--inline {
  min-width: 0;
  border: 1px solid var(--color-td-border);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.96) 0%, rgba(242, 245, 254, 0.55) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.85);
}

.wizard-stepper--inline .stepper-track {
  gap: clamp(6px, 1.2vw, 14px);
}

.wizard-stepper--inline .step-card {
  padding: 2px 0 6px;
}

.wizard-stepper--inline .step-headline {
  margin-bottom: 0;
  font-size: 0.8125rem;
}

.wizard-stepper--inline .step-num {
  width: 22px;
  height: 22px;
  font-size: 0.75rem;
}

/* 内联模式：说明仍保留给读屏，视觉上不占高 */
.wizard-stepper--inline .step-desc--sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

/* —— 侧栏垂直 —— */
.wizard-stepper--sidebar {
  border: none;
  background: transparent;
  box-shadow: none;
}

.sidebar-rail {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.sidebar-step {
  display: grid;
  grid-template-columns: 28px minmax(0, 1fr);
  column-gap: 14px;
  /* 与右侧文案顶部对齐；轨道列单独 stretch 以便竖线铺满行高 */
  align-items: start;
}

.sidebar-step__track {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 28px;
  min-width: 28px;
  align-self: stretch;
  min-height: 0;
}

.sidebar-step__connector {
  width: 2px;
  flex-shrink: 0;
  min-height: 10px;
  border-radius: 999px;
  background: #e2e8f0;
  transition: background 0.2s ease;

  &.is-done {
    background: var(--color-primary);
  }
}

/* 仅向下延伸吸收多余高度，避免与上方一段同时 flex:1 把圆点顶到行垂直居中 */
.sidebar-step__connector--up {
  flex: 0 0 auto;
  margin-bottom: 4px;
}

.sidebar-step__connector--down {
  flex: 1 1 auto;
  margin-top: 4px;
  min-height: 10px;
}

.sidebar-step__node {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  box-sizing: border-box;
  flex-shrink: 0;
  border: 2px solid #e2e8f0;
  background: #fff;
  color: #94a3b8;
  transition:
    border-color 0.2s ease,
    background 0.2s ease,
    color 0.2s ease;
}

.sidebar-step__node.is-done {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: #fff;
}

.sidebar-step__node.is-active {
  border-color: var(--color-primary);
  background: #fff;
  color: var(--color-primary);
  box-shadow: 0 0 0 1px rgba(43, 87, 238, 0.14);
}

.sidebar-step__node.is-pending {
  border-color: #e2e8f0;
  background: #fff;
  color: #94a3b8;
}

.sidebar-step__node-num {
  font-size: 0.75rem;
  font-weight: 700;
  line-height: 1;
}

.sidebar-step__body {
  text-align: left;
  border: none;
  background: transparent;
  padding: 2px 0 18px;
  cursor: pointer;
  border-radius: 10px;
  margin: -4px 0 0;
  padding-left: 2px;
  padding-right: 4px;
  transition: background 0.15s ease;
}

.sidebar-step__body:hover {
  background: rgba(15, 23, 42, 0.03);
}

.sidebar-step__body:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.35);
}

.sidebar-step__title {
  display: block;
  font-size: 0.9375rem;
  font-weight: 700;
  line-height: 1.35;
  color: var(--color-text);
  margin-bottom: 4px;
}

.sidebar-step__body.is-pending .sidebar-step__title {
  color: #64748b;
  font-weight: 600;
}

.sidebar-step__desc {
  display: block;
  font-size: 0.75rem;
  line-height: 1.45;
  color: #94a3b8;
  margin: 0;
  white-space: normal;
}

.sidebar-step__body.is-active .sidebar-step__desc {
  color: #64748b;
}

.stepper-track {
  width: 100%;
  flex-wrap: nowrap;
  align-items: stretch;
  gap: clamp(10px, 2vw, 22px);
}

.step-card {
  position: relative;
  flex: 1 1 0;
  min-width: 0;
  padding: 4px 2px 10px;
  border: none;
  border-radius: 10px;
  background: transparent;
  color: #0f172a;
  cursor: pointer;
  transition:
    background 0.2s ease,
    box-shadow 0.2s ease;
  display: flex;
  flex-direction: column;
}

.step-card:hover {
  background: rgba(15, 23, 42, 0.03);
}

.step-card:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(var(--v-theme-primary), 0.45);
  border-radius: 10px;
}

.step-num {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8125rem;
  font-weight: 700;
  line-height: 1;
  border: 2px solid #e2e8f0;
  background: #f8fafc;
  color: #64748b;
}

.step-card.is-active .step-num {
  border-color: rgb(var(--v-theme-primary));
  background: rgb(var(--v-theme-primary));
  color: #fff;
}

.step-card.is-done .step-num {
  border-color: var(--color-primary);
  background: var(--color-primary);
  color: #fff;
}

.step-card.is-pending .step-num {
  border-color: #e2e8f0;
  background: #fff;
  color: #94a3b8;
}

.step-headline {
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.35;
  color: #1e293b;
  margin-bottom: 6px;
}

.step-card.is-active .step-headline {
  color: rgb(var(--v-theme-primary));
}

.step-card.is-pending .step-headline {
  color: #475569;
  font-weight: 600;
}

.step-card.is-done .step-headline {
  color: #0f172a;
}

.step-headline-text {
  flex: 1 1 auto;
  min-width: 0;
}

.step-desc {
  font-size: 0.8125rem;
  line-height: 1.5;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.step-card.is-active .step-desc {
  color: rgba(var(--v-theme-primary), 0.82);
}

.step-card.is-pending .step-desc {
  color: #94a3b8;
}

.step-card.is-done .step-desc {
  color: #64748b;
}

@media (max-width: 1199px) {
  .wizard-stepper:not(.wizard-stepper--sidebar) .stepper-track {
    flex-wrap: wrap;
    row-gap: 8px;
  }

  .wizard-stepper:not(.wizard-stepper--sidebar) .step-card {
    flex: 1 1 calc(50% - 8px);
    min-width: 160px;
  }
}

@media (max-width: 599px) {
  .wizard-stepper:not(.wizard-stepper--sidebar) .step-card {
    flex: 1 1 100%;
  }

  .step-desc {
    -webkit-line-clamp: 2;
    line-clamp: 2;
  }
}

@media (prefers-reduced-motion: reduce) {
  .step-card {
    transition: none;
  }
}
</style>
